import { useCallback, useEffect, useRef, type CSSProperties } from 'react';
import { Mic } from 'lucide-react';
import { useSpeechRecognition } from '@/shared/use-speech-recognition';

// Mic button paired with a text input. Click to start live speech-to-
// text; click again to stop. Browser-native via Web Speech API — no
// backend, no API key.
//
// Behaviour (post-v2.9.18 polish, post-v2.9.20 inline-interim):
// - Interim text (what the model is hearing right now) writes DIRECTLY
//   into the input field via `onChange`. No popover, no separate
//   preview surface — the textarea is the live preview.
// - When the model finalises a phrase, the interim text in the field
//   becomes "committed" (no visual change, just semantic).
// - When new interim arrives, the OLD interim is stripped from the end
//   of the value and replaced with the new one (so the field doesn't
//   accumulate stale partial transcriptions).
// - If the user types into the input while dictating, the mic detects
//   the value change and stops tracking the old interim portion — so
//   their typing isn't clobbered by the next interim update.
// - Pause-and-continue: the hook auto-restarts SpeechRecognition when
//   the browser auto-ends after silence (unless user explicitly
//   stopped).
// - Visual: while listening, three small dots pulse to the right of
//   the mic icon (clear "live" signal). The mic itself goes red.
//
// Renders nothing in browsers that don't support SpeechRecognition
// (Firefox today). Consumers should not condition rendering on
// `supported` themselves — the button hides itself.

type MicTranscribeButtonProps = {
  // Current value of the paired input. Mic appends to this.
  value: string;
  // Receives the new full value (committed + new interim or final).
  // Wire directly to the input's setter.
  onChange: (next: string) => void;
  // BCP-47 language tag. Defaults to en-US.
  lang?: string;
  // Visual size of the mic icon (px). Button sizes itself proportionally.
  size?: number;
};

export function MicTranscribeButton({
  value,
  onChange,
  lang = 'en-US',
  size = 16,
}: MicTranscribeButtonProps) {
  // Track the up-to-date value synchronously so rapid-fire onresult
  // events (multiple per React tick) all see the latest committed
  // string, not a stale closure capture.
  const valueRef = useRef(value);
  // How many trailing chars of valueRef.current are uncommitted interim
  // text. New interim replaces this slice; final clears it.
  const interimLengthRef = useRef(0);
  // The last value we set via onChange. If the parent's `value` prop
  // arrives different from this, we know the user typed (or some other
  // source mutated the value) — reset interim tracking so we don't
  // strip their typing.
  const lastSetByMicRef = useRef(value);

  useEffect(() => {
    if (value !== lastSetByMicRef.current) {
      // User typed (or external change). Drop interim tracking.
      interimLengthRef.current = 0;
      lastSetByMicRef.current = value;
    }
    valueRef.current = value;
  }, [value]);

  const setValue = useCallback(
    (next: string) => {
      valueRef.current = next;
      lastSetByMicRef.current = next;
      onChange(next);
    },
    [onChange],
  );

  const handleFinalText = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const current = valueRef.current;
      const interimLen = interimLengthRef.current;
      const committed =
        interimLen > 0
          ? current.slice(0, current.length - interimLen).trimEnd()
          : current;
      const separator =
        committed.length === 0 || /\s$/.test(committed) ? '' : ' ';
      const next = committed + separator + trimmed;
      interimLengthRef.current = 0;
      setValue(next);
    },
    [setValue],
  );

  const handleInterimText = useCallback(
    (text: string) => {
      const current = valueRef.current;
      const interimLen = interimLengthRef.current;
      const committed =
        interimLen > 0
          ? current.slice(0, current.length - interimLen).trimEnd()
          : current;

      if (!text) {
        // Empty interim → strip any prior interim, leave committed alone.
        if (interimLen > 0) {
          interimLengthRef.current = 0;
          setValue(committed);
        }
        return;
      }

      const interimTrimmed = text.trimStart();
      const separator =
        committed.length === 0 || /\s$/.test(committed) ? '' : ' ';
      const next = committed + separator + interimTrimmed;
      interimLengthRef.current = (separator + interimTrimmed).length;
      setValue(next);
    },
    [setValue],
  );

  const { state, error, start, stop, supported } = useSpeechRecognition({
    onFinalText: handleFinalText,
    onInterimText: handleInterimText,
    lang,
  });

  if (!supported) return null;

  const listening = state === 'listening' || state === 'starting';
  const isError = state === 'error';

  const onClick = () => {
    if (listening) stop();
    else start();
  };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <button
        type="button"
        onClick={onClick}
        title={
          listening
            ? 'Click to stop dictating'
            : isError
              ? `Mic error: ${error} — click to retry`
              : 'Click to dictate (speech → text)'
        }
        aria-label={listening ? 'Stop dictation' : 'Start dictation'}
        aria-pressed={listening}
        style={buttonStyle(listening, isError)}
      >
        <Mic size={size} strokeWidth={listening ? 2 : 1.75} />
      </button>
      {listening && <ListeningDots />}
    </span>
  );
}

function ListeningDots() {
  return (
    <span
      role="status"
      aria-label="Listening"
      title="Listening… speak now"
      style={{
        display: 'inline-flex',
        gap: 3,
        alignItems: 'center',
        paddingLeft: 2,
        paddingRight: 2,
      }}
    >
      <span style={{ ...dotStyle, animationDelay: '0s' }} aria-hidden="true" />
      <span style={{ ...dotStyle, animationDelay: '0.2s' }} aria-hidden="true" />
      <span style={{ ...dotStyle, animationDelay: '0.4s' }} aria-hidden="true" />
    </span>
  );
}

function buttonStyle(listening: boolean, isError: boolean): CSSProperties {
  const bg = listening
    ? 'var(--state-blocked, #b14545)'
    : 'transparent';
  const color = listening
    ? 'var(--surface-1, #fff)'
    : isError
      ? 'var(--state-blocked, #b14545)'
      : 'var(--ink-secondary)';
  return {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 6,
    background: bg,
    color,
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    fontFamily: 'inherit',
    transition: 'background 120ms ease, color 120ms ease',
  };
}

const dotStyle: CSSProperties = {
  width: 5,
  height: 5,
  borderRadius: '50%',
  background: 'var(--state-blocked, #b14545)',
  animation: 'mic-dot-pulse 1.2s ease-in-out infinite',
};

// Inject the pulse keyframes once. Idempotent via id check.
if (typeof document !== 'undefined' && !document.getElementById('mic-pulse-keyframes')) {
  const style = document.createElement('style');
  style.id = 'mic-pulse-keyframes';
  style.textContent = `
    @keyframes mic-dot-pulse {
      0%, 100% { opacity: 0.25; transform: scale(0.7); }
      50% { opacity: 1; transform: scale(1.1); }
    }
  `;
  document.head.appendChild(style);
}
